<template>
  <section>
    <div class="px-4 pt-6 mx-auto max-w-5xl md:py-12 lg:px-10 lg:pt-16">
      <div class="mx-auto max-w-3xl">
        <h2
          class="font-ww-bold text-2xl leading-tight text-[#3B2314] sm:text-3xl lg:text-34"
          v-html="title"
        />

        <div
          class="mt-6 space-y-5 font-ww-regular text-17 leading-6 text-[#4A4A4A] [&_p+p]:mt-5"
          v-html="introContent"
        />

        <!-- Brain health card -->
        <div class="mt-6 grid overflow-hidden rounded-lg border border-[#E0D5C5] bg-white md:grid-cols-7">
          <div class="relative min-h-70 border-r border-[#E0D5C5] bg-[#2A2A2A] md:col-span-4">
            <nuxt-img
              v-if="brainStatImageSrc"
              :src="brainStatImageSrc"
              :alt="brainStatImageAlt"
              width="422"
              height="285"
              format="webp"
              loading="lazy"
              sizes="xs:100vw md:422px lg:422px"
              class="object-cover absolute inset-0 w-full h-full"
            />

            <div class="flex relative flex-col justify-end p-6 h-full text-white min-h-72 sm:p-8 sm:pb-7">
              <p class="leading-none text-left font-ww-bold text-53" v-html="brainStatValue" />
              <p
                class="mt-4 leading-6 text-left font-ww-regular text-17"
                v-html="brainStatQuote"
              />
            </div>
          </div>

          <div class="flex flex-col justify-center md:col-span-3">
            <div
              class="items-center border-b border-[#E0D5C5] p-6 font-ww-regular text-17 leading-6 text-[#4A4A4A] sm:p-7"
              v-html="brainFact1"
            />
            <div
              class="items-center p-6 font-ww-regular text-17 leading-6 text-[#4A4A4A] sm:p-7"
              v-html="brainFact2"
            />
          </div>
        </div>

        <p
          class="mt-10 font-ww-regular text-17 leading-6 text-[#4A4A4A]"
          v-html="heartTransitionText"
        />

        <!-- Heart health card -->
        <div class="mt-6 grid overflow-hidden rounded-lg border border-[#E0D5C5] bg-white md:grid-cols-2">
          <div class="flex flex-col justify-center border-r border-[#E0D5C5] p-6 text-center sm:p-8">
            <div class="mt-4 w-full">
              <svg class="mx-auto md:mx-0" width="305" height="106" viewBox="0 0 305 106" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M3.01219 93.8557H5.85619L4.44019 89.8357L3.01219 93.8557ZM0.672188 96.9277L3.80419 88.5277H5.08819L8.22019 96.9277H6.96019L6.22819 94.9117H2.64019L1.90819 96.9277H0.672188ZM11.7863 99.4717C10.2623 99.4717 8.97834 98.4397 8.97834 96.9637C8.97834 95.9557 9.65034 95.1157 10.3343 94.7917C9.89034 94.4917 9.36234 93.8917 9.36234 93.0397C9.36234 91.7197 10.4903 90.7837 11.7863 90.7837H14.5223V91.5037L13.5503 91.6717C13.8383 91.9477 14.1503 92.3917 14.1503 93.0757C14.1503 93.8437 13.6343 94.5037 13.1903 94.7677C13.8863 95.0917 14.5343 95.8957 14.5343 96.9637C14.5343 98.4397 13.3103 99.4717 11.7863 99.4717ZM11.7863 94.3837C12.6503 94.3837 13.1303 93.7717 13.1303 93.0637C13.1303 92.3317 12.6383 91.7317 11.7863 91.7317C10.9823 91.7317 10.3823 92.3317 10.3823 93.0637C10.3823 93.7717 10.9703 94.3837 11.7863 94.3837ZM11.7863 98.5237C12.7823 98.5237 13.5143 97.8517 13.5143 96.9277C13.5143 96.0037 12.7703 95.3077 11.7863 95.3077C10.8623 95.3077 10.0343 96.0037 10.0343 96.9277C10.0343 97.8517 10.8383 98.5237 11.7863 98.5237ZM18.7992 97.0717C16.9032 97.0717 15.8232 95.7037 15.8232 93.9277C15.8232 92.1757 16.9392 90.7837 18.7992 90.7837C20.6232 90.7837 21.6432 92.1157 21.6432 93.7957C21.6432 93.9397 21.6312 94.1317 21.6192 94.2757H16.8912C16.9752 95.3797 17.7312 96.0997 18.8472 96.0997C19.6392 96.0997 20.2032 95.7157 20.5992 95.0197L21.4752 95.4517C21.0192 96.3877 20.1072 97.0717 18.7992 97.0717ZM16.9032 93.3997H20.5872C20.5032 92.4157 19.8432 91.7557 18.8112 91.7557C17.7672 91.7557 17.0112 92.4157 16.9032 93.3997ZM29.2153 96.9277V95.0797H25.6033V94.1437L29.4313 88.5277H30.3193V94.0477H31.4473V95.0797H30.3193V96.9277H29.2153ZM26.8153 94.0477H29.2153V90.5437L26.8153 94.0477ZM35.1876 97.0717C33.3876 97.0717 32.1156 95.4517 32.1156 92.7277C32.1156 89.9917 33.3876 88.3837 35.1876 88.3837C36.9756 88.3837 38.2476 89.9917 38.2476 92.7277C38.2476 95.4517 36.9756 97.0717 35.1876 97.0717ZM35.1876 96.0277C36.3636 96.0277 37.1436 94.9117 37.1436 92.7277C37.1436 90.5437 36.3276 89.4277 35.1876 89.4277C34.0596 89.4277 33.2196 90.5437 33.2196 92.7277C33.2196 94.9117 34.0236 96.0277 35.1876 96.0277Z" fill="#4A4A4A"/>
<path d="M84.1372 96.9277V95.0797H80.5252V94.1437L84.3532 88.5277H85.2412V94.0477H86.3692V95.0797H85.2412V96.9277H84.1372ZM81.7372 94.0477H84.1372V90.5437L81.7372 94.0477ZM90.0134 97.0717C88.5494 97.0717 87.5894 96.2077 87.2894 94.9357L88.3094 94.6357C88.5134 95.4397 89.1134 96.0037 90.0254 96.0037C91.0574 96.0037 91.8254 95.2477 91.8254 94.1437C91.8254 93.0277 91.0574 92.2717 90.0254 92.2717C89.3414 92.2717 88.8014 92.6797 88.5494 93.1237L87.6134 92.9077L88.1414 88.5277H92.3534V89.5717H89.0054L88.6934 91.8037C88.9574 91.5157 89.5094 91.2277 90.2054 91.2277C91.7774 91.2277 92.9294 92.4637 92.9294 94.1437C92.9294 95.7757 91.7534 97.0717 90.0134 97.0717Z" fill="#4A4A4A"/>
<path d="M141.732 97.0717C140.268 97.0717 139.308 96.2077 139.008 94.9357L140.028 94.6357C140.232 95.4397 140.832 96.0037 141.744 96.0037C142.776 96.0037 143.544 95.2477 143.544 94.1437C143.544 93.0277 142.776 92.2717 141.744 92.2717C141.06 92.2717 140.52 92.6797 140.268 93.1237L139.332 92.9077L139.86 88.5277H144.072V89.5717H140.724L140.412 91.8037C140.676 91.5157 141.228 91.2277 141.924 91.2277C143.496 91.2277 144.648 92.4637 144.648 94.1437C144.648 95.7757 143.472 97.0717 141.732 97.0717ZM148.484 97.0717C146.684 97.0717 145.412 95.4517 145.412 92.7277C145.412 89.9917 146.684 88.3837 148.484 88.3837C150.272 88.3837 151.544 89.9917 151.544 92.7277C151.544 95.4517 150.272 97.0717 148.484 97.0717ZM148.484 96.0277C149.66 96.0277 150.44 94.9117 150.44 92.7277C150.44 90.5437 149.624 89.4277 148.484 89.4277C147.356 89.4277 146.516 90.5437 146.516 92.7277C146.516 94.9117 147.32 96.0277 148.484 96.0277Z" fill="#4A4A4A"/>
<path d="M170.443 97.0717C168.979 97.0717 168.019 96.2077 167.719 94.9357L168.739 94.6357C168.943 95.4397 169.543 96.0037 170.455 96.0037C171.487 96.0037 172.255 95.2477 172.255 94.1437C172.255 93.0277 171.487 92.2717 170.455 92.2717C169.771 92.2717 169.231 92.6797 168.979 93.1237L168.043 92.9077L168.571 88.5277H172.783V89.5717H169.435L169.123 91.8037C169.387 91.5157 169.939 91.2277 170.635 91.2277C172.207 91.2277 173.359 92.4637 173.359 94.1437C173.359 95.7757 172.183 97.0717 170.443 97.0717ZM174.543 96.9277V95.9197L177.183 93.0277C178.035 92.0917 178.443 91.5757 178.443 90.7717C178.443 90.0157 177.891 89.4277 177.099 89.4277C176.307 89.4277 175.851 90.0277 175.719 90.7357L174.675 90.4117C174.951 89.2477 175.803 88.3837 177.159 88.3837C178.491 88.3837 179.559 89.4517 179.559 90.7117C179.559 91.7797 179.031 92.5717 178.167 93.4957L175.923 95.8717H179.667V96.9277H174.543Z" fill="#F1574B"/>
<path d="M285.395 97.0717C283.811 97.0717 282.611 95.8597 282.611 94.2997C282.611 93.5437 282.959 92.7997 283.463 92.0197L285.755 88.5277H287.051L284.843 91.6717C285.071 91.6237 285.287 91.5877 285.503 91.5877C286.979 91.5877 288.191 92.7277 288.191 94.2997C288.191 95.8597 286.991 97.0717 285.395 97.0717ZM285.395 96.0157C286.391 96.0157 287.075 95.2477 287.075 94.2997C287.075 93.3277 286.391 92.5597 285.395 92.5597C284.387 92.5597 283.715 93.3277 283.715 94.2997C283.715 95.2477 284.399 96.0157 285.395 96.0157ZM289.364 96.9277V95.9197L292.004 93.0277C292.856 92.0917 293.264 91.5757 293.264 90.7717C293.264 90.0157 292.712 89.4277 291.92 89.4277C291.128 89.4277 290.672 90.0277 290.54 90.7357L289.496 90.4117C289.772 89.2477 290.624 88.3837 291.98 88.3837C293.312 88.3837 294.38 89.4517 294.38 90.7117C294.38 91.7797 293.852 92.5717 292.988 93.4957L290.744 95.8717H294.488V96.9277H289.364Z" fill="#4A4A4A"/>
<path d="M199.146 97.0717C197.682 97.0717 196.722 96.2077 196.422 94.9357L197.442 94.6357C197.646 95.4397 198.246 96.0037 199.158 96.0037C200.19 96.0037 200.958 95.2477 200.958 94.1437C200.958 93.0277 200.19 92.2717 199.158 92.2717C198.474 92.2717 197.934 92.6797 197.682 93.1237L196.746 92.9077L197.274 88.5277H201.486V89.5717H198.138L197.826 91.8037C198.09 91.5157 198.642 91.2277 199.338 91.2277C200.91 91.2277 202.062 92.4637 202.062 94.1437C202.062 95.7757 200.886 97.0717 199.146 97.0717ZM205.803 97.0717C204.339 97.0717 203.379 96.2077 203.079 94.9357L204.099 94.6357C204.303 95.4397 204.903 96.0037 205.815 96.0037C206.847 96.0037 207.615 95.2477 207.615 94.1437C207.615 93.0277 206.847 92.2717 205.815 92.2717C205.131 92.2717 204.591 92.6797 204.339 93.1237L203.403 92.9077L203.931 88.5277H208.143V89.5717H204.795L204.483 91.8037C204.747 91.5157 205.299 91.2277 205.995 91.2277C207.567 91.2277 208.719 92.4637 208.719 94.1437C208.719 95.7757 207.543 97.0717 205.803 97.0717Z" fill="#4A4A4A"/>
<path d="M256.692 97.0717C255.108 97.0717 253.908 95.8597 253.908 94.2997C253.908 93.5437 254.256 92.7997 254.76 92.0197L257.052 88.5277H258.348L256.14 91.6717C256.368 91.6237 256.584 91.5877 256.8 91.5877C258.276 91.5877 259.488 92.7277 259.488 94.2997C259.488 95.8597 258.288 97.0717 256.692 97.0717ZM256.692 96.0157C257.688 96.0157 258.372 95.2477 258.372 94.2997C258.372 93.3277 257.688 92.5597 256.692 92.5597C255.684 92.5597 255.012 93.3277 255.012 94.2997C255.012 95.2477 255.696 96.0157 256.692 96.0157ZM263.313 97.0717C261.513 97.0717 260.241 95.4517 260.241 92.7277C260.241 89.9917 261.513 88.3837 263.313 88.3837C265.101 88.3837 266.373 89.9917 266.373 92.7277C266.373 95.4517 265.101 97.0717 263.313 97.0717ZM263.313 96.0277C264.489 96.0277 265.269 94.9117 265.269 92.7277C265.269 90.5437 264.453 89.4277 263.313 89.4277C262.185 89.4277 261.345 90.5437 261.345 92.7277C261.345 94.9117 262.149 96.0277 263.313 96.0277Z" fill="#4A4A4A"/>
<path d="M141.558 61.3645V39.8869H135.422V21.4776C135.422 19.7901 136.023 18.346 137.226 17.1453C138.429 15.9446 139.873 15.3432 141.558 15.3411H153.831C155.519 15.3411 156.964 15.9425 158.167 17.1453C159.369 18.348 159.97 19.7921 159.968 21.4776V39.8869H153.831V61.3645H141.558ZM147.695 12.2729C146.007 12.2729 144.563 11.6726 143.362 10.4719C142.162 9.27117 141.56 7.82604 141.558 6.13647C141.556 4.4469 142.158 3.00279 143.362 1.80414C144.567 0.605484 146.011 0.0041117 147.695 2.07312e-05C149.378 -0.00407023 150.823 0.597302 152.03 1.80414C153.237 3.01097 153.837 4.45508 153.831 6.13647C153.825 7.81785 153.225 9.26299 152.03 10.4719C150.836 11.6807 149.39 12.2811 147.695 12.2729Z" fill="#F1574B"/>
<path d="M253.609 61.5781V43.5781H244.609L253.759 20.4781C254.259 19.2781 255.034 18.3281 256.084 17.6281C257.134 16.9281 258.309 16.5781 259.609 16.5781C260.909 16.5781 262.084 16.9281 263.134 17.6281C264.184 18.3281 264.959 19.2781 265.459 20.4781L274.609 43.5781H265.609V61.5781H253.609ZM255.373 11.8141C254.197 10.6401 253.609 9.22813 253.609 7.57813C253.609 5.92813 254.197 4.51613 255.373 3.34213C256.549 2.16813 257.961 1.58013 259.609 1.57813C261.257 1.57613 262.67 2.16413 263.848 3.34213C265.026 4.52013 265.613 5.93213 265.609 7.57813C265.605 9.22413 265.018 10.6371 263.848 11.8171C262.678 12.9971 261.265 13.5841 259.609 13.5781C257.953 13.5721 256.541 12.9821 255.373 11.8141Z" fill="#D9D9D9"/>
<rect x="0.492188" y="61.3652" width="303.734" height="9.74219" rx="4.87109" fill="#D9D9D9"/>
<path d="M172.703 61.3652H299.355C302.046 61.3652 304.227 63.5461 304.227 66.2363C304.227 68.9266 302.046 71.1074 299.355 71.1074H172.703V61.3652Z" fill="#909291"/>
<path d="M145.148 61.3652V81.9277" stroke="black"/>
<path d="M172.703 61.3652V76.5645" stroke="#F1574B"/>
<path d="M288.688 61.3652V76.5645" stroke="black"/>
<path d="M259.977 61.3652V81.9277" stroke="black"/>
<path d="M87.7344 61.3652V81.9277" stroke="black"/>
<path d="M202.562 61.3652V81.9277" stroke="black"/>
<path d="M30.3203 61.3652V81.9277" stroke="black"/>
<path d="M165.574 11.9726C165.184 12.3631 165.184 12.9963 165.574 13.3868L171.938 19.7508C172.329 20.1413 172.962 20.1413 173.352 19.7508C173.743 19.3602 173.743 18.7271 173.352 18.3365L167.695 12.6797L173.352 7.02283C173.743 6.63231 173.743 5.99914 173.352 5.60862C172.962 5.21809 172.329 5.21809 171.938 5.60862L165.574 11.9726ZM243.996 13.3868C244.387 12.9963 244.387 12.3631 243.996 11.9726L237.632 5.60861C237.242 5.21809 236.609 5.21809 236.218 5.60861C235.827 5.99914 235.827 6.6323 236.218 7.02283L241.875 12.6797L236.218 18.3365C235.827 18.7271 235.827 19.3602 236.218 19.7507C236.609 20.1413 237.242 20.1413 237.632 19.7507L243.996 13.3868ZM166.281 12.6797L166.281 13.6797L243.289 13.6797L243.289 12.6797L243.289 11.6797L166.281 11.6797L166.281 12.6797Z" fill="#F1574B"/>
<path d="M297.523 43.2461C297.523 46.8775 292.633 53.2437 290.127 56.2671C289.369 57.1817 287.998 57.1817 287.24 56.2671C284.735 53.2437 279.844 46.8775 279.844 43.2461C279.844 38.364 283.801 34.4062 288.684 34.4062C293.566 34.4062 297.523 38.364 297.523 43.2461Z" fill="#909291"/>
<circle cx="288.684" cy="43.1055" r="6.41797" fill="#D9D9D9"/>
<path d="M181.539 43.2461C181.539 46.8775 176.648 53.2437 174.142 56.2671C173.384 57.1817 172.014 57.1817 171.256 56.2671C168.75 53.2437 163.859 46.8775 163.859 43.2461C163.859 38.364 167.817 34.4062 172.699 34.4062C177.581 34.4062 181.539 38.364 181.539 43.2461Z" fill="#F1574B"/>
<circle opacity="0.6" cx="172.699" cy="43.1055" r="6.41797" fill="white"/>
</svg>

            </div>
            <p
              class="mt-8 font-ww-bold text-38 leading-none text-[#F1574B]"
              v-html="heartStatValue"
            />
            <p
              class="mt-4 font-ww-regular text-17 leading-6 text-[#4A4A4A]"
              v-html="heartStatText"
            />
          </div>

          <div class="relative min-h-[360px] bg-[#2A2A2A]">
            <nuxt-img
              v-if="heartCardImageSrc"
              :src="heartCardImageSrc"
              :alt="heartCardImageAlt"
              width="370"
              height="360"
              format="webp"
              loading="lazy"
              sizes="xs:100vw md:370px lg:370px"
              class="object-cover object-top absolute inset-0 w-full h-full"
            />

            <div class="flex relative flex-col justify-end p-6 h-full text-white min-h-90 sm:p-8 sm:pb-9">
              <p class="leading-tight font-ww-bold text-34" v-html="heartCardTitle" />
              <p
                class="mt-2 font-ww-regular text-17 leading-6 [&_u]:underline"
                v-html="heartCardSubtitle"
              />
            </div>
          </div>
        </div>

        <div
          class="mt-6 space-y-5 font-ww-regular text-17 leading-6 text-[#4A4A4A] [&_p+p]:mt-5"
          v-html="closingContent"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
defineProps({
  title: {
    type: String,
    default: 'The New Men\'s Health Crisis No One Warns You About (And How To Reverse It)'
  },
  introContent: {
    type: String,
    default: `<p>What do GI Joe and Men's Health Week have in common?</p>
<p>No, this isn't a cheesy joke. In fact, it's actually a rather serious question.</p>
<p>If you grew up in the 80s, then you've likely heard the following line of encouragement from the one and only GI Joe - "knowing is half the battle."</p>
<p>And Men's Health Week? Well, "knowing" is what this annually dedicated time is all about.</p>
<p>Why?</p>
<p>Because bluntly stated, there's just some things you'll have to keep an eye on more closely when it comes to your health.</p>
<p>For instance, brain health is a big one for men.</p>`
  },
  brainStatImageSrc: {
    type: String,
    default: '/assets/images/horizontal-left.png'
  },
  brainStatImageAlt: {
    type: String,
    default: 'Elderly hands clasped together'
  },
  brainStatValue: {
    type: String,
    default: '1.5x'
  },
  brainStatQuote: {
    type: String,
    default: '"Men are 1.5 times more likely to develop Parkinson\'s disease than women." <sup>1</sup>'
  },
  brainFact1: {
    type: String,
    default: 'Even mild cognitive impairment is more common in men than women, especially as you age.<sup>2</sup>'
  },
  brainFact2: {
    type: String,
    default: 'Some studies suggest men\'s brains age faster than women\'s.<sup>3</sup>'
  },
  heartTransitionText: {
    type: String,
    default: 'And, what about heart health?'
  },
  heartStatValue: {
    type: String,
    default: '1 in 4'
  },
  heartStatText: {
    type: String,
    default: 'One in four American men die from cardiovascular events, and men typically have their first heart attack 10 years earlier than women.<sup>4</sup>'
  },
  heartCardImageSrc: {
    type: String,
    default: '/assets/images/vertical-right.png'
  },
  heartCardImageAlt: {
    type: String,
    default: 'Man experiencing chest pain'
  },
  heartCardTitle: {
    type: String,
    default: 'Heart disease'
  },
  heartCardSubtitle: {
    type: String,
    default: 'is not just the leading cause of death globally, it\'s also the <u>leading cause of death</u> in men.<sup>5</sup>'
  },
  closingContent: {
    type: String,
    default: `<p><strong>Sexual health?</strong></p>
<p>Each of these areas (and more) affect men differently, but the thing is, the issues we've mentioned thus far are <strong>preventable!</strong></p>
<p>And, that's the entire point of and purpose behind Men's Health Week - highlighting preventable health concerns so you can either address them early, or better yet, avoid them altogether!</p>
<p>Here, I'm joining arms with you to fight the first half of this battle, looking at the top areas of concern for men's health, and providing you with some of the best ways to support your body.</p>
<p>Then, the next half of the battle (implementation, prioritization) is up to <strong>you!</strong></p>`
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Mens Health Crisis',
  inputs: [
    {
      name: 'title',
      type: 'richText',
      defaultValue: 'The New Men\'s Health Crisis No One Warns You About (And How To Reverse It)'
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p>What do GI Joe and Men's Health Week have in common?</p>
<p>No, this isn't a cheesy joke. In fact, it's actually a rather serious question.</p>
<p>If you grew up in the 80s, then you've likely heard the following line of encouragement from the one and only GI Joe - "knowing is half the battle."</p>
<p>And Men's Health Week? Well, "knowing" is what this annually dedicated time is all about.</p>
<p>Why?</p>
<p>Because bluntly stated, there's just some things you'll have to keep an eye on more closely when it comes to your health.</p>
<p>For instance, brain health is a big one for men.</p>`
    },
    {
      name: 'brainStatImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/horizontal-left.png'
    },
    {
      name: 'brainStatImageAlt',
      type: 'text',
      defaultValue: 'Elderly hands clasped together'
    },
    {
      name: 'brainStatValue',
      type: 'text',
      defaultValue: '1.5x'
    },
    {
      name: 'brainStatQuote',
      type: 'richText',
      defaultValue: '"Men are 1.5 times more likely to develop Parkinson\'s disease than women." <sup>1</sup>'
    },
    {
      name: 'brainFact1',
      type: 'richText',
      defaultValue: 'Even mild cognitive impairment is more common in men than women, especially as you age.<sup>2</sup>'
    },
    {
      name: 'brainFact2',
      type: 'richText',
      defaultValue: 'Some studies suggest men\'s brains age faster than women\'s.<sup>3</sup>'
    },
    {
      name: 'heartTransitionText',
      type: 'richText',
      defaultValue: 'And, what about heart health?'
    },
    {
      name: 'heartStatValue',
      type: 'text',
      defaultValue: '1 in 4'
    },
    {
      name: 'heartStatText',
      type: 'richText',
      defaultValue: 'One in four American men die from cardiovascular events, and men typically have their first heart attack 10 years earlier than women.<sup>4</sup>'
    },
    {
      name: 'heartCardImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/vertical-right.png'
    },
    {
      name: 'heartCardImageAlt',
      type: 'text',
      defaultValue: 'Man experiencing chest pain'
    },
    {
      name: 'heartCardTitle',
      type: 'richText',
      defaultValue: 'Heart disease'
    },
    {
      name: 'heartCardSubtitle',
      type: 'richText',
      defaultValue: 'is not just the leading cause of death globally, it\'s also the <u>leading cause of death</u> in men.<sup>5</sup>'
    },
    {
      name: 'closingContent',
      type: 'richText',
      defaultValue: `<p><strong>Sexual health?</strong></p>
<p>Each of these areas (and more) affect men differently, but the thing is, the issues we've mentioned thus far are <strong>preventable!</strong></p>
<p>And, that's the entire point of and purpose behind Men's Health Week - highlighting preventable health concerns so you can either address them early, or better yet, avoid them altogether!</p>
<p>Here, I'm joining arms with you to fight the first half of this battle, looking at the top areas of concern for men's health, and providing you with some of the best ways to support your body.</p>
<p>Then, the next half of the battle (implementation, prioritization) is up to <strong>you!</strong></p>`
    }
  ]
})
</script>
